import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  Captions, Loader2, X, Check, ToggleLeft, ToggleRight,
  ArrowUp, ArrowDown, Minus, Volume2, Languages, RefreshCcw,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANGUAGE_NAME = {
  en: "English", de: "German", fr: "French", es: "Spanish",
  it: "Italian", el: "Greek", tr: "Turkish", ar: "Arabic",
  nl: "Dutch", ru: "Russian",
};

/* ──────────────────────────────────────────────────────────────────────────
 * TailSubsButton
 *
 * Opens a modal listing every tail clip on a story so the creator can:
 *   • toggle translated-subtitle burn ON/OFF per tail
 *   • move the subtitle line to bottom / top / middle of the frame
 *   • tweak the vertical margin so subs don't sit over a face / paw
 *
 * Changes are saved immediately via PATCH /api/videos/{id}/tail-settings
 * which also purges any cached subtitle burns for the affected tail so
 * the next render picks up the new settings without a manual rebuild.
 * ────────────────────────────────────────────────────────────────────── */
export default function TailSubsButton({ videoId, testidSuffix }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});  // { [filename]: true }
  const [tails, setTails] = useState([]);
  const [sourceLang, setSourceLang] = useState("en");
  const [err, setErr] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNote, setRefreshNote] = useState("");
  const pollRef = useRef(null);

  // Lazy-load on open. We re-fetch each time so we see whatever Whisper
  // has cached since the creator last opened the panel.
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setErr("");
    axios.get(`${API}/videos/${videoId}/tail-settings`)
      .then((r) => {
        setTails(r.data?.tails || []);
        setSourceLang(r.data?.source_language || "en");
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load tail clips."))
      .finally(() => setLoading(false));
  }, [open, videoId]);

  // ESC to close
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const patch = async (filename, partial) => {
    setSaving((s) => ({ ...s, [filename]: true }));
    setErr("");
    try {
      const r = await axios.patch(`${API}/videos/${videoId}/tail-settings`, {
        tail_filename: filename, settings: partial,
      });
      setTails((prev) => prev.map((t) =>
        t.filename === filename
          ? { ...t, settings: { ...t.settings, ...r.data.settings } }
          : t,
      ));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't save.");
    } finally {
      setSaving((s) => { const n = { ...s }; delete n[filename]; return n; });
    }
  };

  // One-click rescue for stories where Whisper mis-identified the
  // tail's source language (e.g. tagged a German speaker as English),
  // which causes the burn pipeline to short-circuit and serve no
  // captions to the other-language tiles. Wipes cached transcripts
  // + on-disk burns so the next render rebuilds with the latest
  // self-heal logic in `tail_subs.subtitle_tail()`.
  const refreshTranscripts = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setRefreshNote("");
    setErr("");
    try {
      const r = await axios.post(`${API}/videos/${videoId}/tail-transcripts/refresh`);
      const cleared = r.data?.transcripts_cleared || 0;
      const burns = r.data?.cached_burns_removed || 0;
      setRefreshNote(
        `Cleared. Now click Re-render on the language tile that's missing captions ` +
        `— Storylo will re-detect the tail's language and burn fresh subs. ` +
        `(Removed ${burns} cached burn${burns === 1 ? "" : "s"}.)`,
      );
      // Re-fetch so the transcripts column shows "not generated yet" again.
      const fresh = await axios.get(`${API}/videos/${videoId}/tail-settings`);
      setTails(fresh.data?.tails || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't refresh transcripts.");
    } finally {
      setRefreshing(false);
    }
  };

  const hasAnyTail = tails.length > 0;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="text-[11px] uppercase tracking-[0.18em] py-2 rounded-lg border border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors flex items-center justify-center gap-1.5"
        data-testid={`library-tail-subs-${testidSuffix}`}
        title="Per-tail subtitle settings (position, on/off)"
      >
        <Captions className="w-3.5 h-3.5" /> Tail subs
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto p-4 md:p-8"
          onClick={() => setOpen(false)}
          data-testid="tail-subs-modal"
        >
          <div
            className="panel max-w-2xl w-full mx-auto my-12 md:my-6 p-6 md:p-7"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Per-tail subtitle settings"
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--accent)] mb-1.5 inline-flex items-center gap-1.5">
                  <Captions className="w-3.5 h-3.5" /> Tail clip subtitles
                </div>
                <div className="font-display text-xl leading-tight">
                  Localise each tail clip
                </div>
                <div className="text-xs text-[color:var(--muted)] mt-1.5 leading-relaxed">
                  Tail clips keep the original speaker's voice intact. When a viewer plays this story in a different language, Storylo burns translated subtitles onto the tail. Toggle them off per clip or move them out of the way of a face / paw.
                </div>
                <div className="text-[10px] text-[color:var(--muted)] mt-2 inline-flex items-center gap-1.5">
                  <Volume2 className="w-3 h-3" /> Story source language: <span className="text-[color:var(--accent)]">{LANGUAGE_NAME[sourceLang] || sourceLang}</span>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-[color:var(--muted)] hover:text-white"
                data-testid="tail-subs-close"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {loading && (
              <div className="flex items-center gap-2 text-xs text-[color:var(--muted)] py-6 justify-center">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading tail clips…
              </div>
            )}

            {!loading && !hasAnyTail && (
              <div className="text-xs text-[color:var(--muted)] py-6 text-center">
                This story has no tail clips uploaded.
              </div>
            )}

            {!loading && hasAnyTail && (
              <ul className="space-y-3" data-testid="tail-subs-list">
                {tails.map((t) => {
                  const tx = t.transcript?.text || "";
                  const txLang = t.transcript?.language || "";
                  const isSaving = !!saving[t.filename];
                  return (
                    <li
                      key={t.filename}
                      className="p-3 rounded-lg border border-white/10 bg-white/[0.02]"
                      data-testid={`tail-row-${t.safe_key}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium">{t.filename}</span>
                            <span className="text-[10px] text-[color:var(--muted)]">
                              {t.duration_seconds ? `${t.duration_seconds.toFixed(1)}s` : ""}
                              {t.size_bytes ? ` · ${(t.size_bytes / 1024 / 1024).toFixed(1)} MB` : ""}
                            </span>
                          </div>
                          {tx ? (
                            <div className="text-[11px] text-[color:var(--muted)] italic line-clamp-2">
                              <Languages className="w-3 h-3 inline mr-1 opacity-60" />
                              <span className="text-[color:var(--accent)] not-italic">{LANGUAGE_NAME[txLang] || txLang || "—"}:</span> {tx}
                            </div>
                          ) : (
                            <div className="text-[11px] text-[color:var(--muted)] italic">
                              Transcript not generated yet — will be detected on the first non-source-language render.
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => patch(t.filename, { subtitles_enabled: !t.settings.subtitles_enabled })}
                          disabled={isSaving}
                          className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] uppercase tracking-[0.15em] transition-colors ${
                            t.settings.subtitles_enabled
                              ? "bg-[color:var(--accent)]/10 border border-[color:var(--accent)]/60 text-[color:var(--accent)]"
                              : "border border-white/15 text-[color:var(--muted)] hover:text-white"
                          }`}
                          data-testid={`tail-toggle-${t.safe_key}`}
                          title="Enable / disable translated subtitle burn for this tail"
                        >
                          {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : t.settings.subtitles_enabled
                              ? <ToggleRight className="w-3.5 h-3.5" />
                              : <ToggleLeft className="w-3.5 h-3.5" />}
                          {t.settings.subtitles_enabled ? "On" : "Off"}
                        </button>
                      </div>

                      {/* Position + Margin controls — only relevant when
                          subs are enabled. Disabled state is a single
                          toggle, no other config needed. */}
                      {t.settings.subtitles_enabled && (
                        <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-white/5">
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--muted)] mb-1.5">Position</div>
                            <div className="grid grid-cols-3 gap-1.5">
                              {[
                                { id: "top", label: "Top", icon: ArrowUp },
                                { id: "middle", label: "Middle", icon: Minus },
                                { id: "bottom", label: "Bottom", icon: ArrowDown },
                              ].map((p) => {
                                const Icon = p.icon;
                                const active = t.settings.position === p.id;
                                return (
                                  <button
                                    key={p.id}
                                    onClick={() => patch(t.filename, { position: p.id })}
                                    disabled={isSaving}
                                    className={`p-1.5 rounded-md text-[10px] inline-flex items-center justify-center gap-1 transition-colors ${
                                      active
                                        ? "bg-[color:var(--accent)]/10 border border-[color:var(--accent)] text-[color:var(--accent)]"
                                        : "border border-white/10 hover:border-white/30 text-[color:var(--muted)]"
                                    }`}
                                    data-testid={`tail-position-${t.safe_key}-${p.id}`}
                                  >
                                    <Icon className="w-3 h-3" /> {p.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <div>
                            <div className="flex items-baseline justify-between mb-1.5">
                              <span className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--muted)]">Margin</span>
                              <span className="text-xs text-[color:var(--accent)]">{t.settings.margin_v}px</span>
                            </div>
                            <input
                              type="range" min={0} max={200} step={4}
                              value={t.settings.margin_v}
                              onChange={(e) => setTails((prev) => prev.map((x) =>
                                x.filename === t.filename
                                  ? { ...x, settings: { ...x.settings, margin_v: Number(e.target.value) } }
                                  : x,
                              ))}
                              onMouseUp={(e) => patch(t.filename, { margin_v: Number(e.target.value) })}
                              onTouchEnd={(e) => patch(t.filename, { margin_v: Number(e.target.value) })}
                              disabled={isSaving}
                              className="w-full accent-[color:var(--accent)]"
                              data-testid={`tail-margin-${t.safe_key}`}
                            />
                            <div className="flex justify-between text-[10px] text-[color:var(--muted)]">
                              <span>0</span><span>100</span><span>200</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {refreshNote && (
              <div className="text-xs text-emerald-300 p-3 rounded border border-emerald-500/30 bg-emerald-500/10 mt-4 leading-relaxed" data-testid="tail-subs-refresh-note">
                {refreshNote}
              </div>
            )}

            {err && (
              <div className="text-xs text-red-300 p-2 rounded border border-red-500/30 bg-red-500/10 mt-4" data-testid="tail-subs-error">{err}</div>
            )}

            {/* Rescue button for the Atila-style bug: Whisper occasionally
                misidentifies a non-English tail's language, the pipeline
                short-circuits, and viewers see no subs. One tap wipes
                the bad cache so the next render re-detects + re-burns. */}
            {hasAnyTail && (
              <div className="mt-5 pt-4 border-t border-white/5">
                <button
                  onClick={refreshTranscripts}
                  disabled={refreshing}
                  className="text-[11px] uppercase tracking-[0.18em] py-2 px-3 rounded-md border border-white/10 hover:border-amber-300/60 hover:text-amber-300 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                  data-testid="tail-subs-refresh"
                  title="Use this if a tail is missing captions in some languages — wipes Whisper's cached transcript so the next render re-detects the language."
                >
                  {refreshing
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <RefreshCcw className="w-3.5 h-3.5" />}
                  Refresh tail transcripts
                </button>
                <p className="text-[10px] text-[color:var(--muted)] mt-2 leading-relaxed">
                  Captions missing in some languages? Tap above, then re-render the affected language tile. Storylo will re-detect each tail's spoken language and burn fresh subtitles.
                </p>
              </div>
            )}

            <div className="text-[10px] text-[color:var(--muted)] mt-5 pt-4 border-t border-white/5 leading-relaxed">
              Saved changes invalidate any cached subtitle burns for that tail. The next time a viewer requests a language render, Storylo rebuilds the tail with your new layout — usually takes ~10s per tail.
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
